import * as React from 'react';

// REDUX
import { useAppSelector } from '../../store';

// COMPONENTS
import Button from '../../components/Button';
import CircleImage from '../../components/CircleImage';
import DisplayDateTime from '../../components/DisplayDateTime';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';

// TYPES
import { SafetyChecklists } from './SafetyChecklistsSubmit';
import { GlobalLookupsDomainChecklistFrequency } from '../../api/models';
import { GlobalLookupsDomainChecklistAssignmentType } from '../../api/models';

interface StepTwoTypes {
	safetyChecklistsFormData: SafetyChecklists;
	setSafetyChecklistsFormData: React.Dispatch<React.SetStateAction<SafetyChecklists>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
	formSubmitHandler: () => void;
}

const StepTwo: React.FC<StepTwoTypes> = ({
	safetyChecklistsFormData,
	setSafetyChecklistsFormData,
	setActiveStep,
	formSubmitHandler
}) => {
	const { title, frequency, startDate, endDate, assignmentType, recipients, saveAsTemplate, checklistItems } =
		safetyChecklistsFormData;

	const checklistFrequencies = useAppSelector((state) => state.lists.checklistFrequencies) || [];

	const frequencyLabel =
		checklistFrequencies.find(
			(frequencyItem: GlobalLookupsDomainChecklistFrequency) => frequencyItem.value === frequency
		)?.label || frequency;

	const renderedChecklistItems = checklistItems?.filter((item) => item.text !== '');

	const removeRecipientHandler = (id: string) => {
		setSafetyChecklistsFormData((prev) => ({
			...prev,
			recipientUserIds: (prev.recipientUserIds || []).filter((uid) => uid !== id),
			recipients: (prev.recipients || []).filter((recipient) => recipient.id !== id)
		}));
	};

	const handleSaveAsTemplateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = (e.target as HTMLInputElement | null)?.checked ?? false;

		setSafetyChecklistsFormData((prev) => ({
			...prev,
			saveAsTemplate: checked
		}));
	};

	return (
		<>
			<h2 className="general-header">Please review your safety checklists.</h2>

			<p className="sub-grey-text mbtom-48">
				Once submitted your checklist will be sent to your nominated staff member for approval.
			</p>

			<div className="general-form-layout-wrapper">
				<div className="complaints-detail w-100">
					<div className="detail-block">
						<h2 className="body-large">Schedule</h2>

						{frequencyLabel && <p className="fw-medium text-grey-5 mtop-16">{frequencyLabel}</p>}

						{startDate && (
							<p className="fw-medium text-grey-5 mtop-16">
								Start date: <DisplayDateTime format="DD/MM/YYYY">{startDate}</DisplayDateTime>
							</p>
						)}

						{endDate && (
							<p className="fw-medium text-grey-5 mtop-16">
								End Date: <DisplayDateTime format="DD/MM/YYYY">{endDate}</DisplayDateTime>
							</p>
						)}
					</div>

					<div className="general-divider mtop-30 mbtom-30"></div>

					{assignmentType && (
						<div className="detail-block">
							<h4 className="body-large mbtom-16">Assignment and completion</h4>

							{assignmentType === 'Individual' && (
								<>
									<label className="mbtom-8 fw-medium text-grey-5">Individual recipients</label>

									<p className="mb-0 fw-medium text-grey-4">
										Assign to singular or multiple users. Each recipient is required to complete the checklist.
									</p>
								</>
							)}

							{assignmentType === 'Group' && (
								<>
									<label className="mbtom-8 fw-medium text-grey-5">Group recipients</label>

									<p className="mb-0 fw-medium text-grey-4">
										Assigned to multiple users, only one recipient from the group is required to complete the checklist.
									</p>
								</>
							)}
						</div>
					)}

					<div className="detail-block">
						<h3 className="body-medium fw-bold mtop-30 mbtom-20">List of recipients</h3>

						<div className="general-divider mbtom-16"></div>

						{recipients && recipients.length > 0 ? (
							<section className="info-section incident-form mtop-0 mtop-0 bg-white rounded border-grey-3 p-3 max-height-550 w-100-424">
								<ul>
									{recipients.map((item, index) => (
										<li key={item.id} className="row-container no-hover">
											<div className={`row detail-row ${index === recipients.length - 1 ? 'border-bottom-0' : ''}`}>
												<div className="col-2 pdl-0">
													<CircleImage firstName={item.name} lastName={item.name} src={item.profileImage} />
												</div>

												<div className="col-8 pdl-0">
													<div className="row detail-info center">
														<div className="col-12 detail-name">
															<p className="detail-name mbtom-0">{item.name}</p>

															<p className="label fw-bold mbtom-0">{item.role}</p>
														</div>
													</div>
												</div>

												<div className="col-2 link-group pdr-0">
													<Button
														className="btn-text pdr-0 mtop-0"
														onClick={() => item.id && removeRecipientHandler(item.id)}
													>
														Remove
													</Button>
												</div>
											</div>
										</li>
									))}
								</ul>
							</section>
						) : (
							<p className="detail-info">None added</p>
						)}
					</div>

					<div className="general-divider mbtom-30"></div>

					<h4 className="body-large mbtom-30">Checklist</h4>

					{title && (
						<div className="detail-block">
							<h2 className="detail-heading">Title</h2>

							<p className="detail-info fw-medium">{title}</p>
						</div>
					)}

					{renderedChecklistItems &&
						renderedChecklistItems.length > 0 &&
						renderedChecklistItems.map((item, index) => (
							<div key={item.id || index} className="detail-block">
								{item.text && (
									<>
										<h2 className="detail-heading">Item {index + 1}</h2>

										<p className="detail-info fw-medium">{item.text}</p>
									</>
								)}
							</div>
						))}

					<div className="general-divider mbtom-30"></div>

					<div className="detail-block">
						<div className="general-radio-row mb-0">
							<input
								id="saveAsTemplateReview"
								type="checkbox"
								checked={saveAsTemplate ?? false}
								onChange={handleSaveAsTemplateChange}
							/>

							<div className="option-info">
								<label htmlFor="saveAsTemplateReview">Save as an organisational checklist template</label>

								<p className="info-sub">
									Are you sure you wish to save this as a template? Organisational checklist templates will be available
									for all users. <br />
									Please note, only the checklist title and items will be saved. The schedule, assignment and recipient
									details will not be saved.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="general-divider mt-0 mbtom-30"></div>

			<div className="form-two-button-group">
				<Button
					buttonIcon={<ArrowLeftIcon className="link-icon" />}
					className="btn-secondary edit-btn"
					onClick={() => setActiveStep('step1')}
				>
					Edit
				</Button>

				<Button className="btn-primary submit-btn w-auto" onClick={formSubmitHandler}>
					Submit checklist
				</Button>
			</div>
		</>
	);
};

export default StepTwo;
